/**
 * The shell every §F6 tool renders inside — the mockup's `toolShell()` plus the run
 * plumbing all six of the dock's forms would otherwise repeat. (Buffer and overlay, Part
 * H's other two, left the dock in Task 10 — client items 11/12, UI-only.)
 *
 * Layout is the mockup's, in the mockup's order: "← All tools", the tile + name, the
 * description, the form, `#adProg`, `#adResult`.
 *
 * Three behaviours are here rather than in each tool, because six copies of a rule is six
 * chances to differ:
 *
 * * **the progress bar and the "running" line** show for exactly the phases that are
 *   running, and carry §D6's own percentage;
 * * **§F6's "Errors → toast + inline message"** — *both*, once per error. The inline
 *   message is the one that survives; the toast is what makes a reader who has scrolled the
 *   dock notice. The server's sentence is already localised (§D6 honours `?lang=`), so it
 *   is printed as it arrived; only the client's own two verdicts carry a translatable
 *   sentinel — `TIMED_OUT` when the poll gives up, and `OVERSIZED` when §E7 spilled the
 *   result to an artefact the browser could not then fetch (`lib/analysis.ts`).
 * * **the §E7 cache note.** A second identical run answers instantly, and a panel that
 *   filled in with no progress bar at all looks like it ignored the button.
 */
import { useEffect, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from '../../lib/icons';
import { OVERSIZED, TIMED_OUT, toolCard, type RunPhase, type ToolId } from '../../lib/analysis';
import { toast } from '../../lib/toast';
import { ProgressBar } from './ProgressBar';

export interface ToolFrameProps {
  tool: ToolId;
  onBack: () => void;
  phase: RunPhase;
  progress: number;
  error: string | null;
  cached?: boolean;
  /** The params form. */
  children: ReactNode;
  /** The result panels — `null` until there is a result. */
  result?: ReactNode;
}

export function ToolFrame({
  tool,
  onBack,
  phase,
  progress,
  error,
  cached = false,
  children,
  result = null,
}: ToolFrameProps) {
  const { t } = useTranslation();
  const card = toolCard(tool);
  const message =
    error === TIMED_OUT
      ? t('analysis.timedOut')
      : error === OVERSIZED
        ? t('analysis.resultOversized')
        : error;

  // One toast per error, not one per render: the panel re-renders on every keystroke in
  // the form beneath it, and §F2's toast is a single instance that would be re-raised for
  // as long as the message stayed on screen.
  const toasted = useRef<string | null>(null);
  useEffect(() => {
    if (phase !== 'error') {
      toasted.current = null;
      return;
    }
    const text = message ?? t('analysis.failed');
    if (toasted.current === text) return;
    toasted.current = text;
    toast(text);
  }, [phase, message, t]);

  return (
    <div className="ad-form">
      <button type="button" className="ad-back" id="adBack" onClick={onBack}>
        ← {t('analysis.back')}
      </button>
      <h3 className="ad-title">
        <span className="tool-ico" style={{ background: card.color }} aria-hidden="true">
          <Icon name={card.icon} size={14} />
        </span>
        {t(`analysis.tool.${tool}.name`)}
      </h3>
      <p className="ad-desc">{t(`analysis.tool.${tool}.desc`)}</p>

      {children}

      {phase === 'running' ? (
        <>
          <ProgressBar value={progress} label={t('analysis.progress')} />
          <div className="ad-status" data-role="running">
            {t('analysis.queued')}
          </div>
        </>
      ) : null}

      {phase === 'error' ? (
        <div className="note" data-role="error" role="alert">
          {message ?? t('analysis.failed')}
        </div>
      ) : null}

      {cached && phase === 'done' ? (
        <div className="tbl-note" data-role="cached">
          {t('analysis.cached')}
        </div>
      ) : null}

      <div id="adResult">{result}</div>
    </div>
  );
}

export default ToolFrame;
